import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { source } from '../../../app/data/source'
import type { Place, PlacesResponse } from '../../../shared/contract'

type SearchState = { term: string; results: Place[]; status: 'idle' | 'searching' | 'ready' | 'offline' | 'error' }
const empty: SearchState = { term: '', results: [], status: 'idle' }
const keyFor = (term: string) => ['v5-place-search', source.kind, term] as const

/** The existing keyless endpoint; unlike worker RPC, its network request can actually be aborted. */
export async function searchPlaces(term: string, signal: AbortSignal): Promise<PlacesResponse> {
  if (source.kind === 'mock') {
    const result = await source.places(term)
    signal.throwIfAborted()
    return result
  }
  const response = await fetch(`/v2/places?q=${encodeURIComponent(term)}&limit=10`, { signal, headers: { Accept: 'application/json' } })
  if (!response.ok) throw new Error(`Place search unavailable (${response.status})`)
  const body: unknown = await response.json()
  if (!body || typeof body !== 'object' || !('results' in body) || !Array.isArray(body.results)) throw new Error('Invalid place response')
  const results = body.results.filter((p): p is Place => p && typeof p.name === 'string' && typeof p.region === 'string' && typeof p.label === 'string' && Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
  return { results }
}

/** Raw input invalidates immediately, before debounce. Identity also rejects sources that ignore abort. */
export function usePlaceLookup(query: string, open: boolean, offline: boolean) {
  const client = useQueryClient()
  const identity = useRef(0)
  const [state, setState] = useState<SearchState>(empty)
  const [retry, setRetry] = useState(0)
  const term = query.trim().toLowerCase().slice(0, 80)
  useEffect(() => {
    const id = ++identity.current
    const controller = new AbortController()
    if (!open || term.length < 2) { setState(empty); return () => controller.abort() }
    const cached = client.getQueryData<PlacesResponse>(keyFor(term))
    if (cached) { setState({ term, results: cached.results, status: 'ready' }); return () => controller.abort() }
    if (offline && source.kind !== 'mock') { setState({ term, results: [], status: 'offline' }); return () => controller.abort() }
    setState({ term, results: [], status: 'searching' })
    const timer = window.setTimeout(() => {
      void searchPlaces(term, controller.signal).then((result) => {
        if (controller.signal.aborted || identity.current !== id) return
        client.setQueryData(keyFor(term), result)
        setState({ term, results: result.results, status: 'ready' })
      }).catch(() => {
        if (!controller.signal.aborted && identity.current === id) setState({ term, results: [], status: 'error' })
      })
    }, 180)
    return () => { controller.abort(); window.clearTimeout(timer) }
  }, [term, open, offline, client, retry])
  // Never expose a previous term, even in the render before effect cleanup.
  return { ...(state.term === term ? state : { term, results: [], status: term.length < 2 ? 'idle' as const : 'searching' as const }), retry: () => setRetry((n) => n + 1) }
}
